<template>
  <div class="tag-info" :class="{'tag-info--hidden': isTagInfoHidden}" v-if="value._id">
    <div class="tag-info__inner">
      <div class="tag-info__toggle">
        <button class="btn-toggle" :class="{'btn-toggle--slide-active': isSlideSelected}" @click="isTagInfoHidden = !isTagInfoHidden">
          <fa v-if="isTagInfoHidden" :icon="['fas', 'chevron-left']"/>
          <fa v-else :icon="['fas', 'chevron-right']"/>
          <div class="m-auto">Edit&nbsp;{{ value.type === 'slide' ? 'Slide' : 'Tag' }}</div>
        </button>
      </div>
      <div class="tag-info__col">
        <div v-if="value.type !== 'slide'" class="tag-info__block">
          <label>Type</label>
          <select v-model="value.type" class="form-control">
            <option value="basic">Basic</option>
            <option value="basicIcon">Basic Icon</option>
            <option value="box">Box</option>
            <option value="rich">Rich</option>
            <option value="reference">Reference</option>
            <option value="reveal">Reveal</option>
            <option value="action">Action</option>
          </select>
        </div>
        <div v-if="['basic', 'basicIcon', 'box', 'rich', 'reference', 'reveal', 'action', 'slide'].indexOf(value.type) !== -1" class="tag-info__block">
          <label>Label</label>
          <input
            v-model="value.label[activeLanguage.code]"
            class="form-control"
            type="text"
            placeholder="label"
          />
        </div>
        <div v-if="value.pos" class="tag-info__block tag-info__block--inline">
          <label>X</label>
          <input type="number" v-model.number="value.pos.x" class="form-control" />
        </div>
        <div v-if="value.pos" class="tag-info__block tag-info__block--inline">
          <label>Y</label>
          <input type="number" v-model.number="value.pos.y" class="form-control" />
        </div>
        <div v-if="value.pos" class="tag-info__block tag-info__block--inline tag-info__block--inactive">
          <label>W</label>
          <input type="text" v-model.number="width" readonly />
        </div>
        <div v-if="value.pos" class="tag-info__block tag-info__block--inline tag-info__block--inactive">
          <label>H</label>
          <input type="text" v-model.number="height" readonly />
        </div>
        <div v-if="['slide'].indexOf(value.type) !== -1" class="tag-info__block">
          <label>Should slide expand</label>
          <toggle-button
            v-model="value.shouldSlideExpand"
            color="#28a745"
            :sync="true"
            :labels="false"
            :width="40"
            :height="20"
            style="font-weight: 400"
          />
        </div>
      </div>

      <div class="tag-info__col element-grow">
        <div v-if="['box'].indexOf(value.type) !== -1" class="tag-info__block">
          <label>distance</label>
          <input
            v-model="value.distance[activeLanguage.code]"
            class="form-control"
            type="text"
            placeholder="distance"
          />
        </div>
        <div v-if="['box'].indexOf(value.type) !== -1" class="tag-info__block">
          <label>Arrow degrees</label>
          <input
            v-model.number="value.arrowDeg"
            class="form-control"
            type="number"
            placeholder="arrow degrees"
          />
        </div>
        <div v-if="['box', 'action', 'rich'].indexOf(value.type) !== -1" class="tag-info__block">
          <label>Icon Code 
            <info-box class="ml-1">
              For a full list of codes head to <a href="https://fontawesome.com/cheatsheet" target="_blank">https://fontawesome.com/cheatsheet</a>
            </info-box>
          </label>
          <div class="icon-selector">
            <div class="icon-selector__input icon-selector__input--preview">
              <input v-model="value.icon" class="form-control" type="text" placeholder="Icon code">
              <div class="icon-selector__preview">
                <fa v-if="value.icon" :icon="['fas', value.icon]"/>
              </div>
            </div>
          </div>
        </div>
        <div v-if="['action', 'rich'].indexOf(value.type) !== -1" class="tag-info__block" :key="value.action.method">
          <label>Assign Action</label>
          <select v-model="value.action.method" class="form-control" @change="methodChanged">
            <option value="">Unassigned</option>
            <option value="TRIGGER_CHANGE_BACKGROUND">Change Background</option>
            <option value="TRIGGER_SHOW_ASSET">Show Asset</option>
            <option value="TRIGGER_NAV_ROUTER">Navigate to page</option>
          </select>
          <input-asset v-if="value.action.method === 'TRIGGER_CHANGE_BACKGROUND'" class="mt-2" v-model="value.action.payload[0]"/>
          <input-asset v-if="value.action.method === 'TRIGGER_SHOW_ASSET'" class="mt-2" :multiple="true" v-model="value.action.payload"/>
          <div v-if="value.action.method === 'TRIGGER_SHOW_ASSET'" class="mt-2" style="width: 100%;">
            <label>Virtual Tour Url
              <info-box class="ml-1">
                The last asset will open virtual tour url when clicked
              </info-box>
            </label>
            <input
              v-model="value.virtualTourUrl"
              class="form-control"
              type="text"
              placeholder=""
            />
          </div>
          <select v-if="value.action.method === 'TRIGGER_NAV_ROUTER'" v-model="value.action.payload[0]" class="form-control mt-2">
            <option value="">Select a page</option>
            <option v-for="page in pages" :key="page._id" :value="page._id">{{ page.heading.en }}</option>
          </select>
        </div>
        <div v-if="['reference'].indexOf(value.type) !== -1" class="tag-info__block">
          <label>Order override
            <info-box class="ml-1">
              Set a custom order for a tag from low to high. Numbers can be negative.
            </info-box>
          </label>
          <input
            v-model="value.overrideOrder"
            class="form-control"
            type="text"
            placeholder="0"
          />
        </div>
        <div v-if="['reference'].indexOf(value.type) !== -1" class="tag-info__block">
          <label>Is Visible</label>
          <toggle-button 
            v-model="value.isVisible" 
            color="#28a745" 
            :sync="true" 
            :labels="false" 
            :width="40" 
            :height="20" 
            style="font-weight: 400"
          />
        </div>
        <div v-if="['rich', 'reference', 'reveal'].indexOf(value.type) !== -1" class="tag-info__block element-grow">
          <label>description</label>
          <textarea
            v-model="value.description[activeLanguage.code]"
            class="form-control element-grow"
            rows="3"
            placeholder="description"
          ></textarea>
        </div>
        <div v-if="['rich'].indexOf(value.type) !== -1" class="tag-info__block element-grow">
          <label>More Information</label>
          <textarea
            v-model="value.moreInformation[activeLanguage.code]"
            class="form-control element-grow"
            rows="3"
            placeholder="more information"
          ></textarea>
        </div>
        <div v-if="['rich'].indexOf(value.type) !== -1" class="tag-info__block element-grow">
          <label>QR URL</label>
          <input
            v-model="value.qrUrl"
            class="form-control"
            type="text"
            placeholder="https://"
          />
        </div>
        <div v-if="['basic'].indexOf(value.type) !== -1" class="tag-info__block">
          <label>Colour</label>
          <select v-model="value.colour" class="form-control">
            <option value="black">Black</option>
            <option value="blue">Blue</option>
            <option value="orange">Orange</option>
          </select>
        </div>
        <div v-if="['basic', 'reference', 'rich', 'action'].indexOf(value.type) !== -1" class="tag-info__block">
          <label>Tail Position</label>
          <select v-model="value.tailPosition" class="form-control">
            <option value="none">No Tail</option>
            <option value="bottom-left">Bottom Left</option>
            <option value="bottom-center">Bottom Center</option>
            <option value="bottom-right">Bottom Right</option>
            <option value="top-left">Top Left</option>
            <option value="top-center">Top Center</option>
            <option value="top-right">Top Right</option>
            <option value="left-center">Left Center</option>
            <option value="right-center">Right Center</option>
          </select>
        </div>
        <div v-if="['basic', 'reference', 'rich', 'action'].indexOf(value.type) !== -1 && value.tailPosition !== 'none'" class="tag-info__block">
          <label>Tail Height</label>
          <input
            v-model="value.tailHeight"
            class="form-control"
            type="text"
            placeholder="4"
          />
        </div>
        <div v-if="['rich'].indexOf(value.type) !== -1" class="tag-info__block">
          <label>Flip</label>
          <toggle-button 
            v-model="value.flip" 
            color="#28a745" 
            :sync="true" 
            :labels="false" 
            :width="40" 
            :height="20" 
            style="font-weight: 400"
          />
        </div>
        <div v-if="['rich'].indexOf(value.type) !== -1" class="tag-info__block">
          <label>Overlay Content</label>
          <toggle-button 
            v-model="value.overlayContent" 
            color="#28a745" 
            :sync="true" 
            :labels="false" 
            :width="40" 
            :height="20" 
            style="font-weight: 400"
          />
        </div>
        <div v-if="['rich'].indexOf(value.type) !== -1 && value.overlayContent" class="tag-info__block">
          <label>Overlay Opacity</label>
          <input
            v-model="value.overlayOpacity"
            class="form-control"
            step=".1"
            min="0"
            max="1"
            type="number"
            placeholder=".5"
          />
        </div>
        <div v-if="['basic'].indexOf(value.type) !== -1" class="tag-info__block">
          <label>Highlight Tag</label>
          <toggle-button 
            v-model="value.isHighlighted" 
            color="#28a745" 
            :sync="true" 
            :labels="false" 
            :width="40" 
            :height="20" 
            style="font-weight: 400"
          />
        </div>
        <div v-if="value.type === 'slide'" class="tag-info__col">
          <div class="tag-info__block">
            <label>Background Image Override</label>
            <input-asset v-model="value.backgroundImageOverride" defaultCategory="tagImage"></input-asset>
          </div>
        </div>
      </div>

      <div v-if="['rich', 'reference', 'reveal'].indexOf(value.type) !== -1" class="tag-info__col">
        <div class="tag-info__block">
          <label>Asset</label>
          <input-asset v-model="value.assetId" defaultCategory="tagImage" :returnid="true"></input-asset>
        </div>
      </div>

      <div v-if="value.type === 'slide'" class="tag-info__col">
        <div class="tag-info__block">
          <label>Slide Media</label>
          <input-asset :returnid="true" v-model="value.assetId" defaultCategory="tagImage"></input-asset>
        </div>
      </div>

      <div class="tag-info__col tag-info__col--last">
        <button class="btn btn-danger" @click="$emit('remove')">Remove</button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { FETCH_PAGES } from '@/store/actions.type'
import InfoBox from '@/components/InfoBox'
import InputAsset from '@/components/InputAsset'

export default {
  components: { InfoBox, InputAsset },

  props: ['value', 'isSlideSelected'],

  data () {
    return {
      height: 0,
      width: 0,

      isTagInfoHidden: true
    }
  },

  computed: {
    ...mapGetters([
      'pages'
    ]),

    activeLanguage () {
      return this.$store.getters.activeLanguage
    },
  },

  watch: {
    value: {
      deep: true,
      immediate: false,
      handler (newValue) {
        if (newValue.type === 'slide') {
          this.$emit('updateSlide', newValue)
        }

        if (!newValue._id) {
          this.isTagInfoHidden = true
        }
        
        this.updateTagSize()
      }
    }
  },

  created () {
    this.$store.dispatch(FETCH_PAGES)
  },

  methods: {
    updateTagSize () {
      const el = document.getElementById(this.value._id)
      if (el) {
        this.height = Math.round(el.getBoundingClientRect().height / this.$attrs.scale)
        this.width = Math.round(el.getBoundingClientRect().width / this.$attrs.scale)
      }
    },

    methodChanged (e) {
      if (e.target.value === 'TRIGGER_CHANGE_BACKGROUND') {
        this.value.action.payload = [""]
      }

      if (e.target.value === 'TRIGGER_SHOW_ASSET') {
        this.value.action.payload = []
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/styles/_mixins.scss";

.btn-toggle {
  align-items: center;
  appearance: none;
  background: #3182ce;
  border: none;
  border-bottom-left-radius: .25rem;
  color: #fff;
  display: flex;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: .025em;
  padding: 12px 14px;
  width: 140px;

  &--slide-active {
    background: #28a745;
  }
}

.tag-info {
  background: rgba(255, 255, 255, 0.95);
  bottom: 0;
  padding: 15px;
  position: absolute;
  right: 0;
  top: 0;
  transition: all 300ms ease-in-out;
  width: 300px;
  z-index: 100;

  &--hidden {
    transform: translateX(100%);
  }

  &__inner {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: auto;
    width: 100%;
  }

  &__toggle {
    position: absolute;
    right: 100%;
    top: 0;
  }

  &__col {
    display: flex;
    flex-direction: column;
    margin: 0 0 5px 0;
    width: 100%;

    &--last {
      margin: auto 0 0 auto;
      max-width: 100%;
    }
  }

  &__block {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    margin-bottom: 10px;

    label {
      font-size: .85em;
      margin: 0;
      text-transform: capitalize;
    }

    &--inline {
      align-items: center;
      flex-direction: row;
      justify-content: space-between;

      input {
        width: calc(100% - 20px);
      }
    }

    &--inactive {
      input {
        border: none;
        cursor: default;
      }
    }

    .icon-selector {
      display: flex;
      // margin: 0 -6px;
      position: relative;
      width: 100%;

      &__input {
        // padding: 0 6px;
        width: 100%;

        &--preview input {
          padding-right: 30px; 
        }
      }

      &__preview {
        align-items: center;
        background: rgba(0, 0, 0, .15);
        border-radius: 0 3px 3px 0;
        display: flex;
        height: 100%;
        justify-content: center;
        overflow: hidden;
        position: absolute;
        right: 0;
        top: 0;
        width: 30px;
      }
    }
  }
}

.element-grow {
  flex-grow: 1;
}

.input-asset {
  width: 100%;
}
</style>